import type { Provider, ProviderEnrichment } from '@app/src/types/organizations'
import type { EnrichedValue } from '@app/src/utils/providerEnrichmentUtils'
import { getCompletedEnrichmentsLatestFirst, getEnrichedValue } from '@app/src/utils/providerEnrichmentUtils'
import { useMemo } from 'react'

export type { EnrichedValue }

export type ProviderEnrichedValues = {
  websiteAddress: EnrichedValue<string>
  industry: EnrichedValue<string>
  industryId: EnrichedValue<number>
  subIndustry: EnrichedValue<string>
  subIndustryId: EnrichedValue<number>
  registrationNumber: EnrichedValue<string>
  vatNumber: EnrichedValue<string>
  revenueRange: EnrichedValue<string>
  companySizeRange: EnrichedValue<string>
  latestEnrichment: ProviderEnrichment | undefined
}

export function useProviderEnrichedValues(provider: Provider): ProviderEnrichedValues {
  return useMemo(() => {
    const org = provider.organization
    const enrichments = getCompletedEnrichmentsLatestFirst(provider)

    const industryId = getEnrichedValue(
      org?.industryId,
      provider.industryId,
      enrichments.map(e => e.industryId),
    )
    const subIndustryId = getEnrichedValue(
      org?.subIndustryId,
      provider.subIndustryId,
      enrichments.map(e => e.subIndustryId),
    )

    return {
      websiteAddress: getEnrichedValue(
        org?.websiteAddress,
        provider.websiteAddress,
        enrichments.map(e => e.websiteDomain),
      ),
      industry: {
        ...getEnrichedValue(
          org?.industryNaceCode?.description,
          provider.industry?.description,
          enrichments.map(e => e.industry?.description),
        ),
        isEnriched: industryId.isEnriched,
      },
      industryId,
      subIndustry: {
        ...getEnrichedValue(
          org?.subIndustryNaceCode?.description,
          provider.subIndustry?.description,
          enrichments.map(e => e.subIndustry?.description),
        ),
        isEnriched: subIndustryId.isEnriched,
      },
      subIndustryId,
      registrationNumber: getEnrichedValue(
        org?.registrationNumber,
        provider.registrationNumber,
        enrichments.map(e => e.registrationNumber),
      ),
      vatNumber: getEnrichedValue(
        org?.vatNumber,
        provider.vatNumber,
        enrichments.map(e => e.vatNumber),
      ),
      revenueRange: getEnrichedValue(
        org?.revenueRange,
        provider.revenueRange,
        enrichments.map(e => e.revenueRange),
      ),
      companySizeRange: getEnrichedValue(
        org?.companySizeRange,
        provider.companySizeRange,
        enrichments.map(e => e.companySizeRange),
      ),
      latestEnrichment: enrichments[0],
    }
  }, [provider])
}
